import React, { useMemo, useRef } from "react";

import { Box } from "@chakra-ui/react";
import { IIndividualDetails } from "api/individual-user-management/types";
import { useIndividuals } from "contexts/individuals-context";
import { AddressVerification } from "features/individual-compliance/selected-individual/AddressVerification";
import { BvnAndNinInformation } from "features/individual-compliance/selected-individual/BvnAndNinInformation";
import { EducationAndIncomeDetails } from "features/individual-compliance/selected-individual/EducationAndIncomeDetails";
import { FaceVerification } from "features/individual-compliance/selected-individual/FaceVerification";
import { IndividualComplianceTrail } from "features/individual-compliance/selected-individual/IndividualComplianceTrail";
import { IndividualUserDetails } from "features/individual-compliance/selected-individual/IndividualUserDetails";
import { useElementWidth } from "hooks/useElementWidth";

import { Accordion } from "ui/accordion";
import { IAccordionItem } from "ui/accordion/types";
// import { Loader } from "ui/loader";
import { Heading4 } from "ui/typography";

const LG_WIDTH = 745;
const MD_WIDTH = 600;
const SM_WIDTH = 400;

export const ComplianceDetails = () => {
  const { individualDetails: selectedIndividualDetails } = useIndividuals();
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const isContentWrapperBelow745 = useElementWidth(contentWrapperRef, LG_WIDTH);
  const isContentWrapperBelow600 = useElementWidth(contentWrapperRef, MD_WIDTH);
  const isContentWrapperBelow450 = useElementWidth(contentWrapperRef, SM_WIDTH);

  const accordionItems: IAccordionItem[] = useMemo(
    () => [
      {
        value: "userDetails",
        header: (
          <Box className="flex gap-[10px]">
            <Heading4
              fontWeight="700"
              lineHeight="24px"
              textColor="#00171A"
            >
              User Details
            </Heading4>
          </Box>
        ),
        body: (
          <IndividualUserDetails
            individual={selectedIndividualDetails as IIndividualDetails}
            // status={status}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
            isContentWrapperBelow450={isContentWrapperBelow450}
          />
        ),
      },
      {
        value: "bvnAndNinInformation",
        header: (
          <Box className="flex gap-[10px]">
            <Heading4
              fontWeight="700"
              lineHeight="24px"
              textColor="#00171A"
            >
              BVN and NIN Information
            </Heading4>
          </Box>
        ),
        body: (
          <BvnAndNinInformation
            individual={selectedIndividualDetails as IIndividualDetails}
            // hideResetBvnButton={true}
            // status={status}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
            isContentWrapperBelow450={isContentWrapperBelow450}
          />
        ),
      },
      {
        value: "faceAndIdVerification",
        header: (
          <Box className="flex gap-[10px]">
            <Heading4
              fontWeight="700"
              lineHeight="24px"
              textColor="#00171A"
            >
              Face Verification
            </Heading4>
          </Box>
        ),
        body: (
          <FaceVerification
            individual={selectedIndividualDetails as IIndividualDetails}
            // hideResetFaceButton={true}
            // status={status}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
            isContentWrapperBelow450={isContentWrapperBelow450}
          />
        ),
      },
      {
        value: "educationAndIncomeDetails",
        header: (
          <Box className="flex gap-[10px]">
            <Heading4
              fontWeight="700"
              lineHeight="24px"
              textColor="#00171A"
            >
              Education & Income Details
            </Heading4>
          </Box>
        ),
        body: (
          <EducationAndIncomeDetails
            individual={selectedIndividualDetails as IIndividualDetails}
            // status={status}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
            isContentWrapperBelow450={isContentWrapperBelow450}
          />
        ),
      },
      {
        value: "addressVerification",
        header: (
          <Box className="flex gap-[10px]">
            <Heading4
              fontWeight="700"
              lineHeight="24px"
              textColor="#00171A"
            >
              Address Verification
            </Heading4>
          </Box>
        ),
        body: (
          <AddressVerification
            individual={selectedIndividualDetails as IIndividualDetails}
            hideApprovalButton={true}
            // status={status}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
            isContentWrapperBelow450={isContentWrapperBelow450}
          />
        ),
      },
      {
        value: "complianceTrail",
        header: (
          <Heading4
            fontWeight="700"
            lineHeight="24px"
            textColor="#00171A"
          >
            Compliance Trail
          </Heading4>
        ),
        body: (
          <IndividualComplianceTrail
            individual={selectedIndividualDetails as IIndividualDetails}
            // status={status}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
            isContentWrapperBelow450={isContentWrapperBelow450}
          />
        ),
      },
    ],
    [
      isContentWrapperBelow745,
      isContentWrapperBelow600,
      isContentWrapperBelow450,
      selectedIndividualDetails,
    ]
  );

  return (
    <Box
      className={`rounded-[20px] bg-white ${(isContentWrapperBelow600 && status === "pending") || (isContentWrapperBelow450 && status === "rejected") ? "h-[calc(100%-38%-16px)]" : "h-[calc(100%-133px)]"}`}
      css={{
        boxShadow: "0px 0px 5px 0px #0000000D",
      }}
      ref={contentWrapperRef}
    >
      <Accordion
        accordionItems={accordionItems}
        accordionRootCss={{
          overflow: "hidden scroll",
          height: "100%",
          padding: "1.5rem",
        }}
      />
    </Box>
  );
};
